import { useState, useEffect, useCallback } from "react";
import { Box, Container, Text, Button, Paper, Flex, Tabs, SimpleGrid, Tooltip, Loader, Center } from "@mantine/core";
import { IconInfoCircle, IconPigMoney, IconRosetteDiscountCheck, IconCoins } from "@tabler/icons-react";
import TitleComponent from "@/components/shared/TitleComponent";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { useNavigate } from "react-router-dom";
import CreditsConsumedTab from "./components/CreditsConsumed";
import PurchaseHistoryTab from "./components/PurchaseHistory";
import { getCurrencyForCountry } from "@/shared/utils/formatCurrency";

/* ─────────────── Component ─────────────── */

export default function CreditsPage() {
  const { hasProfile, userDetails } = useUserContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string | null>("consumed");

  const [balanceData, setBalanceData] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [creditPriceInr, setCreditPriceInr] = useState<number | undefined>(undefined);
  const [creditPriceUsd, setCreditPriceUsd] = useState<number | undefined>(undefined);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  const currency = getCurrencyForCountry(userDetails?.company?.headquartersCountry);

  const fetchData = useCallback(async () => {
    if (hasProfile === null) return;

    try {
      setLoadingBalance(true);
      setLoadingTransactions(true);

      const [balance, txns, config] = await Promise.all([
        trpc.wallet.getBalance.query(),
        trpc.wallet.getTransactions.query({}),
        trpc.paymentConfig.get.query(),
      ]);

      setBalanceData(balance);
      setTransactions(txns);
      setCreditPriceInr(config.creditPriceInr);
      setCreditPriceUsd(config.creditPriceUsd);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoadingBalance(false);
      setLoadingTransactions(false);
    }
  }, [hasProfile]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Split transactions into consumed (DEBIT) and purchased (CREDIT/TOPUP + SIGNUP_BONUS)
  const consumedTransactions = transactions.filter((t) => t.direction === "DEBIT");
  const purchaseTransactions = transactions.filter(
    (t) => t.direction === "CREDIT" && (t.reason === "TOPUP" || t.reason === "SIGNUP_BONUS")
  );

  if (loadingBalance) {
    return (
      <Box mih="100vh" pb={40}>
        <TitleComponent
          title="Credits"
          description="Track and view your available credits and usage of credits for collaborations and other actions."
        />
        <Center py="xl" mih="50vh">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  return (
    <Box mih="100vh" pb={40}>
      <TitleComponent
        title="Credits"
        description="Track and view your available credits and usage of credits for collaborations and other actions."
        rightSection={
          <Button size="sm" radius="md" onClick={() => navigate("/credits/buy")}>
            Buy Credits
          </Button>
        }
      />

      <Container size="fluid" px="lg">
        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="md" mb="xl">
          <Paper
            p="md"
            radius="md"
            style={{
              backgroundColor: "var(--mantine-color-primary-0)",
            }}
          >
            <Text fz="lg" fw={400} mb="xs">
              {balanceData?.totalPurchased ?? 0}
            </Text>
            <Flex align="center" gap={6}>
              <IconPigMoney size={18} color="var(--mantine-color-primary-6)" />
              <Text fz="sm" c="slate.6" fw={400}>
                Total Credits Purchased
              </Text>
              <Tooltip label="Total credits purchased via payments" withArrow>
                <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
              </Tooltip>
            </Flex>
          </Paper>

          <Paper
            p="md"
            radius="md"
            style={{
              backgroundColor: "var(--mantine-color-primary-0)",
            }}
          >
            <Text fz="lg" fw={400} mb="xs">
              {balanceData?.totalSpent ?? 0}
            </Text>
            <Flex align="center" gap={6}>
              <IconRosetteDiscountCheck size={18} color="var(--mantine-color-primary-6)" />
              <Text fz="sm" c="slate.6" fw={400}>
                Credits Consumed
              </Text>
              <Tooltip label="Total credits used for verification, collaborations, and NDA" withArrow>
                <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
              </Tooltip>
            </Flex>
          </Paper>

          <Paper
            p="md"
            radius="md"
            style={{
              backgroundColor: "var(--mantine-color-primary-0)",
            }}
          >
            <Text fz="lg" fw={400} mb="xs">
              {balanceData?.available ?? 0}
            </Text>
            <Flex align="center" gap={6}>
              <IconCoins size={18} color="var(--mantine-color-primary-6)" />
              <Text fz="sm" c="slate.6" fw={400}>
                Credit Balance
              </Text>
              <Tooltip label="Available credits remaining" withArrow>
                <IconInfoCircle size={16} style={{ cursor: "pointer" }} />
              </Tooltip>
            </Flex>
          </Paper>
        </SimpleGrid>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab
              value="consumed"
              px="md"
              style={{
                color: activeTab === "consumed" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
              }}
            >
              Credits Consumed
            </Tabs.Tab>
            <Tabs.Tab
              value="purchases"
              px="md"
              style={{
                color: activeTab === "purchases" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
              }}
            >
              Purchase History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="consumed" pt="xl">
            <CreditsConsumedTab transactions={consumedTransactions} loading={loadingTransactions} />
          </Tabs.Panel>

          <Tabs.Panel value="purchases" pt="xl">
            <PurchaseHistoryTab
              transactions={purchaseTransactions}
              loading={loadingTransactions}
              creditPriceInr={creditPriceInr}
              creditPriceUsd={creditPriceUsd}
              currency={currency}
            />
          </Tabs.Panel>
        </Tabs>
      </Container>
    </Box>
  );
}
